import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { open as openParticipantsPane } from '../../../participants-pane/actions';
import { approveKnockingParticipant, rejectKnockingParticipant } from '../../actions';
import { getKnockingParticipants } from '../../functions';
const useStyles = makeStyles()(theme => ({
    container: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        '@media (max-width: 768px)': {
            bottom: '80px',
            right: '50%',
            transform: 'translateX(50%)',
            width: 'calc(100% - 32px)',
            maxWidth: '360px'
        }
    },
    popup: {
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        minWidth: '300px',
        animation: 'slideIn 0.3s ease-out',
        '@keyframes slideIn': {
            from: {
                opacity: 0,
                transform: 'translateY(20px)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        },
        '@media (max-width: 768px)': {
            minWidth: 'unset',
            width: '100%'
        }
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
    },
    avatar: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: '#0376DA',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.125rem',
        fontWeight: 600,
        flexShrink: 0
    },
    avatarImage: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        objectFit: 'cover'
    },
    info: {
        flex: 1,
        minWidth: 0
    },
    name: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    message: {
        fontSize: '0.875rem',
        color: '#666',
        margin: '4px 0 0 0'
    },
    actions: {
        display: 'flex',
        gap: '8px',
        marginTop: '4px'
    },
    button: {
        flex: 1,
        padding: '10px 16px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    acceptButton: {
        background: '#0376DA',
        color: '#fff',
        '&:hover': {
            background: '#0260b8'
        }
    },
    rejectButton: {
        background: '#f5f5f5',
        color: '#dc3545',
        '&:hover': {
            background: '#ffe0e0'
        }
    },
    multipleContainer: {
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        minWidth: '300px',
        animation: 'slideIn 0.3s ease-out',
        '@media (max-width: 768px)': {
            minWidth: 'unset',
            width: '100%'
        }
    },
    multipleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
    },
    multipleIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: '#0376DA',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.25rem',
        fontWeight: 600
    },
    multipleInfo: {
        flex: 1
    },
    multipleTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: 0
    },
    multipleMessage: {
        fontSize: '0.875rem',
        color: '#666',
        margin: '4px 0 0 0'
    },
    viewButton: {
        width: '100%',
        padding: '10px 16px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        background: '#0376DA',
        color: '#fff',
        transition: 'all 0.2s ease',
        '&:hover': {
            background: '#0260b8'
        }
    }
}));
const KnockingParticipantPopup = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const knockingParticipants = useSelector(getKnockingParticipants);
    const handleAccept = useCallback((participantId) => {
        dispatch(approveKnockingParticipant(participantId));
    }, [dispatch]);
    const handleReject = useCallback((participantId) => {
        dispatch(rejectKnockingParticipant(participantId));
    }, [dispatch]);
    const handleViewLobby = useCallback(() => {
        dispatch(openParticipantsPane());
    }, [dispatch]);
    if (knockingParticipants.length === 0) {
        return null;
    }
    return (React.createElement("div", { className: classes.container }, knockingParticipants.map(participant => {
        const initials = participant.name
            ? participant.name.substring(0, 2).toUpperCase()
            : '?';
        return (React.createElement("div", { className: classes.popup, key: participant.id },
            React.createElement("div", { className: classes.header },
                participant.loadableAvatarUrl ? (React.createElement("img", { alt: participant.name, className: classes.avatarImage, src: participant.loadableAvatarUrl })) : (React.createElement("div", { className: classes.avatar }, initials)),
                React.createElement("div", { className: classes.info },
                    React.createElement("p", { className: classes.name }, participant.name),
                    React.createElement("p", { className: classes.message }, t('notify.participantWantsToJoin')))),
            React.createElement("div", { className: classes.actions },
                React.createElement("button", { className: cx(classes.button, classes.acceptButton), onClick: () => handleAccept(participant.id), type: "button" }, t('participantsPane.actions.admit')),
                React.createElement("button", { className: cx(classes.button, classes.rejectButton), onClick: () => handleReject(participant.id), type: "button" }, t('participantsPane.actions.reject')))));
    })));
};
export default KnockingParticipantPopup;
